import { SystemColor } from "@common/declarations";
import { type FetchOptionsArgs, type FetchOptionsResult } from "@common/hocs/withLazy";
import { useI18n } from "@common/hooks/i18n";
import date from "@common/lib/date";
import { formatMoneyDisplay } from "@common/lib/format";
import { i18nFormat } from "@common/lib/i18n";
import { type Account } from "@common/modules/account/types";
import classNames from "classnames";
import React from "react";
import { type ActivityItem, type ActivityProps, LazyActivity } from "./component";
import { getTransactionItemIcon } from "./lib";

export type TransactionActivityProps = Omit<ActivityProps<any>, "context" | "options"> & {
  title?: string;
  refreshKey?: string;
  getItems: (args: FetchOptionsArgs) => Promise<Account.Api.AccountTransaction[]>;
};
const TransactionActivity = (props: TransactionActivityProps) => {
  const i18n = useI18n();

  const fetchOptions = React.useCallback(
    async (args: FetchOptionsArgs): Promise<FetchOptionsResult<ActivityItem>> => {
      const items = await props.getItems(args);

      return {
        items: items.map<ActivityItem>((x) => ({
          id: x.accountTransactionId,
          icon: getTransactionItemIcon(x),
          color: x.accountTransactionStatusIsSuccessful ? SystemColor.Gray : SystemColor.Red,
          title: x.accountTransactionTypeLabel,
          description: (
            <div className="description">
              <span>
                {x.orderNumber
                  ? i18nFormat(i18n.accountTransactionOrderInfo, ["NUMBER", x.orderNumber])
                  : x.description ??
                    (x.cycleEndDate
                      ? i18nFormat(i18n.accountTransactionCycleInfo, ["CYCLE", date.formatDate(x.cycleEndDate)])
                      : "")}
              </span>
              <span
                className={classNames("balance", {
                  positive: x.accountTransactionTypeAdjustment === "+",
                  failed: !x.accountTransactionStatusIsSuccessful
                })}
              >
                {x.accountTransactionTypeAdjustment}&nbsp;
                {formatMoneyDisplay(x.amount, x.accountCurrencyCode)}
              </span>
            </div>
          ),
          date: x.createdOnDate
        })),
        hasMore: items.length === args.size
      };
    },
    [i18n, props.getItems]
  );

  return (
    <LazyActivity
      {...props}
      filter={props.title}
      className="transaction"
      fetchOptions={fetchOptions}
      refreshKey={props.refreshKey}
    />
  );
};
export default TransactionActivity;
